import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { forgotPasswordSchema, type ForgotPasswordFormData } from '../../utils/validation';

const ForgotPasswordForm: React.FC = () => {
  const [emailSent, setEmailSent] = useState(false);
  const { forgotPassword, isLoading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    clearError();
    try {
      await forgotPassword(data.email);
      setEmailSent(true);
    } catch (err) {
      console.error('Forgot password failed:', err);
    }
  };

  React.useEffect(() => {
    return () => clearError();
  }, [clearError]);

  if (emailSent) {
    return (
      <div className="min-h-[133.33vh] flex">
        {/* Left Section - Aesthetic Image Background */}
        <div className="hidden lg:flex lg:w-1/2 pl-6 py-6">
          <div className="relative w-full h-full overflow-hidden rounded-3xl">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070&auto=format&fit=crop')"
              }}
            ></div>
            <div className="absolute inset-0 bg-primary-900/40 backdrop-blur-[2px]"></div>
            <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
              <div className="flex items-center gap-1">
                <div className="w-16 h-16 flex items-center justify-center">
                  <img src="/logo.png" alt="SubRack Logo" className="w-full h-full object-contain" />
                </div>
                <span className="text-2xl font-bold tracking-tight">SubRack</span>
              </div>
              <div className="space-y-6 max-w-lg">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/10 text-sm font-medium">
                  <span className="mr-2">✨</span> Password Recovery
                </div>
                <h1 className="text-5xl font-bold leading-tight">
                  Check your inbox for next steps.
                </h1>
                <p className="text-lg text-primary-50/90 leading-relaxed">
                  We've sent you an email with instructions to reset your password and regain access to your account.
                </p>
              </div>
              <div className="flex items-center gap-4 text-sm font-medium text-primary-50/80">
                <span>© 2024 SubRack</span>
                <div className="w-1 h-1 rounded-full bg-primary-50/40"></div>
                <span>Privacy Policy</span>
                <div className="w-1 h-1 rounded-full bg-primary-50/40"></div>
                <span>Terms of Service</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section - Success Message */}
        <div className="flex-1 flex items-center justify-center p-8 bg-white">
          <div className="w-full max-w-md">
            <div className="flex items-center gap-1 mb-8">
              <div className="w-14 h-14 flex items-center justify-center">
                <img src="/logo.png" alt="SubRack Logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-indigo-600 bg-clip-text text-transparent">
                SubRack
              </span>
            </div>

            <div className="text-center mb-8">
              <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Check your email
              </h2>
              <p className="text-gray-600">
                We've sent a password reset link to{' '}
                <span className="font-medium text-gray-900">
                  {getValues('email')}
                </span>
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-700 text-center">
                Didn't receive the email? Check your spam folder or try again.
              </p>

              <button
                onClick={() => setEmailSent(false)}
                className="w-full py-3 px-4 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                Try different email
              </button>

              <div className="pt-4 border-t">
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back to sign in
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[133.33vh] flex">
      {/* Left Section - Aesthetic Image Background */}
      <div className="hidden lg:flex lg:w-1/2 pl-6 py-6">
        <div className="relative w-full h-full overflow-hidden rounded-3xl">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?q=80&w=2070&auto=format&fit=crop')"
            }}
          ></div>
          <div className="absolute inset-0 bg-primary-900/40 backdrop-blur-[2px]"></div>
          <div className="relative z-10 flex flex-col justify-between p-12 text-white h-full">
            <div className="flex items-center gap-1">
              <div className="w-16 h-16 flex items-center justify-center">
                <img src="/logo.png" alt="SubRack Logo" className="w-full h-full object-contain" />
              </div>
              <span className="text-2xl font-bold tracking-tight">SubRack</span>
            </div>
            <div className="space-y-6 max-w-lg">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/10 text-sm font-medium">
                <span className="mr-2">✨</span> Password Recovery
              </div>
              <h1 className="text-5xl font-bold leading-tight">
                Reset your password securely.
              </h1>
              <p className="text-lg text-primary-50/90 leading-relaxed">
                Enter your email and we'll send you instructions to reset your password and regain access to your account.
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm font-medium text-primary-50/80">
              <span>© 2024 SubRack</span>
              <div className="w-1 h-1 rounded-full bg-primary-50/40"></div>
              <span>Privacy Policy</span>
              <div className="w-1 h-1 rounded-full bg-primary-50/40"></div>
              <span>Terms of Service</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-1 mb-8">
            <div className="w-14 h-14 flex items-center justify-center">
              <img src="/logo.png" alt="SubRack Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-indigo-600 bg-clip-text text-transparent">
              SubRack
            </span>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Forgot password?</h2>
            <p className="text-gray-600">
              Enter your email and we'll send you a reset link
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <input
                  {...register('email')}
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  className={`block w-full pl-10 pr-3 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-white ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-primary-600 to-indigo-600 text-white font-medium rounded-xl hover:from-primary-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 shadow-lg shadow-primary-600/20 disabled:opacity-70 disabled:cursor-not-allowed transform active:scale-[0.99]"
            >
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Sending...</span>
                </div>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>

          {/* Back to Login */}
          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;